/* WHEN TO LEAVE — the question this app is for, asked directly.
 *
 * ═══ WHAT EVERY COMPETITOR MAKES YOU DO ═══
 *
 * Pick a time, solve, read the answer, decide it is bad, pick another time,
 * solve again. Navionics will not even tell you why one is worse — you tap a
 * current arrow and read a table. savvy navvy lists candidate departures
 * without showing what each one costs. Nobody solves the whole day and ranks
 * it, which is odd, because the router is already there and the only thing
 * missing is a loop.
 *
 * This is the loop. Solve the same route at every candidate hour across the
 * window, rank the answers by how long the passage ACTUALLY takes, and say why
 * each one is what it is.
 *
 * ═══ WHY IT RANKS ON ELAPSED TIME AND NOT ON WAITING ═══
 *
 * A plan with no wait is not automatically better. Leaving at 04:00 to catch a
 * fair tide with no hold can be slower overall than leaving at 09:00, holding
 * forty minutes at Dodd, and carrying the flood the rest of the way. The
 * skipper cares when they arrive; the wait is a fact about the plan, not a
 * penalty in its own right.
 *
 * So: total elapsed first, then less waiting as a tie-break — between two plans
 * that get you there at the same minute, the one that keeps moving is the one
 * people want — then earlier departure, so a tie is resolved toward getting on
 * with it.
 *
 * ═══ AND IT SAYS WHAT IT DID NOT KNOW ═══
 *
 * `currentsUsed` false means the model field did not reach that departure, so
 * the passage was timed on the narrows and boat speed alone. That is a
 * perfectly useful plan and a materially weaker claim, and the difference is
 * invisible unless it is carried through to the ranking. Same rule the layers
 * panel follows: absence is a fact, not a failure.
 */

export interface DepartureOutcome {
  /** When this plan leaves. */
  departT: number;
  /** When it arrives, or null if the passage cannot be made at all. */
  arriveT: number | null;
  /** Total time held for slack across the passage. */
  waitMs: number;
  /** False when no surface-current model covered this departure. */
  currentsUsed: boolean;
  /** Set when the router gave up — a stream stronger than the boat can make
   *  good, or a gate with no prediction left. The text is the router's. */
  blocked?: string | null;
}

export interface RankedDeparture extends DepartureOutcome {
  /** Departure to arrival, waits included. Null when blocked. */
  elapsedMs: number | null;
  /** How much slower than the best usable option. 0 for the best; null when
   *  blocked or when nothing is usable. */
  worseByMs: number | null;
  /** True for the single best usable option. */
  best: boolean;
}

/**
 * The times to try.
 *
 * ═══ WHY THE STEP IS AN HOUR AND NOT TEN MINUTES ═══
 *
 * Each candidate is a full worker solve. At ten minutes a two-day window is 288
 * of them, which is minutes of spinning for a list nobody reads past the top
 * five. An hour is 48, and it is also the right RESOLUTION: the thing being
 * aimed at is a two-hour slack window, so a departure an hour either side of
 * the best one lands in the same window and produces the same plan.
 *
 * The fine tuning is the tideline's job — drag the bar and it re-solves at ten
 * minute snaps. This picks the hour; that places you inside it.
 */
export function candidateDepartures(
  from: number,
  windowMs: number,
  stepMs = 3_600_000
): number[] {
  if (!Number.isFinite(from) || windowMs <= 0 || stepMs <= 0) return [];
  /* Anchored to the top of the hour, so the list reads 06:00, 07:00 rather than
   * 06:23, 07:23 — a departure time nobody chose is one people distrust. */
  const first = Math.ceil(from / stepMs) * stepMs;
  const out: number[] = [];
  for (let t = first; t <= from + windowMs; t += stepMs) out.push(t);
  return out;
}

/** Departure to arrival, waits included. Null when the passage is blocked. */
export function elapsedOf(o: DepartureOutcome): number | null {
  if (o.arriveT === null || o.blocked) return null;
  return o.arriveT - o.departT;
}

/**
 * Rank solved outcomes, best first.
 *
 * Blocked ones are kept and sorted last rather than dropped: "there is no way
 * through before Tuesday" is an answer, and a list that silently omits half the
 * day looks like a list of every option.
 */
export function rankDepartures(outcomes: readonly DepartureOutcome[]): RankedDeparture[] {
  const withElapsed = outcomes.map((o) => ({ ...o, elapsed: elapsedOf(o) }));
  const usable = withElapsed.filter((o) => o.elapsed !== null);
  const bestMs = usable.length
    ? Math.min(...usable.map((o) => o.elapsed as number))
    : null;

  const sorted = [...withElapsed].sort((a, b) => {
    /* Blocked last, and among themselves by departure so the list still reads
       chronologically at the bottom. */
    if (a.elapsed === null && b.elapsed === null) return a.departT - b.departT;
    if (a.elapsed === null) return 1;
    if (b.elapsed === null) return -1;
    if (a.elapsed !== b.elapsed) return a.elapsed - b.elapsed;
    /* Between two plans that arrive at the same minute, the one that keeps
       moving. */
    if (a.waitMs !== b.waitMs) return a.waitMs - b.waitMs;
    /* And a tie is resolved toward getting on with it. */
    return a.departT - b.departT;
  });

  let bestTaken = false;
  return sorted.map(({ elapsed, ...o }) => {
    const isBest = !bestTaken && elapsed !== null && elapsed === bestMs;
    if (isBest) bestTaken = true;
    return {
      ...o,
      elapsedMs: elapsed,
      worseByMs: elapsed === null || bestMs === null ? null : elapsed - bestMs,
      best: isBest
    };
  });
}

export interface DepartureWindow extends RankedDeparture {
  /** The last departure in the run that gives this same outcome. Equal to
   *  `departT` for a window of one. */
  untilT: number;
  /** How many candidate hours it covers. */
  count: number;
}

/**
 * Merge consecutive candidates that produce the SAME plan, then rank.
 *
 * ═══ WHY THIS EXISTS ═══
 *
 * The first version of the planner listed every candidate hour, and a
 * screenshot of it showed seven consecutive rows reading "3h18 · no wait". That
 * is not a ranked list, it is a table — and a table is what savvy navvy already
 * shows and what this was supposed to improve on.
 *
 * It is also the wrong ANSWER. Nobody wants to know that 11:00 works and 12:00
 * works and 13:00 works; they want to know that **anything between 11:00 and
 * 19:00 gets you there in 3h18**. A window is a decision you can act on with
 * the rest of the morning still free. An instant is a departure you now have to
 * hit.
 *
 * Merged on the CHRONOLOGICAL list, before ranking, because "consecutive" is a
 * fact about the clock and ranking destroys that order. Two runs an hour apart
 * with the same outcome are still two windows if a worse hour sits between
 * them — that gap is the tide turning, and hiding it would be a lie.
 */
export function rankDepartureWindows(
  outcomes: readonly DepartureOutcome[],
  stepMs = 3_600_000
): DepartureWindow[] {
  const byTime = [...outcomes].sort((a, b) => a.departT - b.departT);

  /** The same plan, for merging purposes: same wait, same duration, same
   *  confidence, same blockage. Duration rather than arrival, because
   *  successive departures arrive successively later by definition. */
  const same = (a: DepartureOutcome, b: DepartureOutcome) =>
    a.waitMs === b.waitMs &&
    a.currentsUsed === b.currentsUsed &&
    (a.blocked ?? null) === (b.blocked ?? null) &&
    elapsedOf(a) === elapsedOf(b);

  const runs: { first: DepartureOutcome; untilT: number; count: number }[] = [];
  for (const o of byTime) {
    const last = runs[runs.length - 1];
    /* `1.5` rather than an exact step: a candidate list can have a gap where a
       solve was skipped, and treating that as contiguous would claim a window
       covers an hour nobody checked. */
    const contiguous = last !== undefined && o.departT - last.untilT <= stepMs * 1.5;
    if (last && contiguous && same(last.first, o)) {
      last.untilT = o.departT;
      last.count++;
    } else {
      runs.push({ first: o, untilT: o.departT, count: 1 });
    }
  }

  const ranked = rankDepartures(runs.map((r) => r.first));
  /* Re-attached by departure rather than by index: `rankDepartures` re-orders. */
  const spanOf = new Map(runs.map((r) => [r.first.departT, r]));
  return ranked.map((r) => {
    const run = spanOf.get(r.departT);
    return { ...r, untilT: run?.untilT ?? r.departT, count: run?.count ?? 1 };
  });
}

/**
 * A one-line reason, for the row.
 *
 * The whole point of ranking is answering "why is that one better", and a list
 * of durations does not. Written from the plan's own facts rather than from the
 * comparison, so it stays true if the list is re-sorted.
 */
export function describeDeparture(o: RankedDeparture): string {
  if (o.blocked) return o.blocked;
  if (o.arriveT === null) return 'No route at this departure';
  const parts: string[] = [];
  if (o.waitMs <= 0) parts.push('no wait');
  else parts.push(`holds ${fmtShort(o.waitMs)}`);
  if (!o.currentsUsed) parts.push('narrows only — no current model this far out');
  return parts.join(' · ');
}

/** "45m", "1h20". Local to this module: the panels format their own headline
 *  numbers, and this is the small print inside a row. */
function fmtShort(ms: number): string {
  const m = Math.round(ms / 60_000);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}`;
}

/**
 * Solve every candidate, in order, with a budget.
 *
 * ═══ SEQUENTIAL, AND THAT IS NOT LAZINESS ═══
 *
 * There is ONE router worker. Firing 48 solves at it concurrently does not make
 * them finish sooner — it makes the first answer arrive after the last request
 * has been queued, so a progressive list cannot be shown and a cancellation
 * cannot take effect. In order, each result lands as soon as it is ready.
 *
 * `onresult` is called per solve so a panel can fill in as it goes. A planner
 * that shows nothing for eleven seconds and then everything is one people
 * assume has hung.
 *
 * `signal` is honoured between solves rather than inside them — the worker
 * protocol has no cancel — so aborting costs at most one wasted solve. That
 * matters because changing the boat speed re-asks the whole question.
 */
export async function planDepartures(
  candidates: readonly number[],
  solve: (departT: number) => Promise<DepartureOutcome>,
  opts: { onresult?: (o: DepartureOutcome, done: number, total: number) => void; signal?: AbortSignal } = {}
): Promise<DepartureOutcome[]> {
  const out: DepartureOutcome[] = [];
  for (const t of candidates) {
    if (opts.signal?.aborted) break;
    const o = await solve(t);
    out.push(o);
    opts.onresult?.(o, out.length, candidates.length);
  }
  return out;
}
